import React, { useState } from "react";

import { Icon, Badge, Button } from "@culturehq/components";
import { useProfile, useQueryString } from "app/context";
import { ProfileLink } from "app/links";
import { useExpand } from "app/hooks";
import { Trans, getUniqueUsers, getQueryStringKey } from "app/utils";
import { getIcon } from "constants/tagsTypes";

import PostStory from "components/stories/post-stories-v2/PostStory";
import LightboxStories from "components/common/gallery/LightboxStories";
import StoryQuestionTitle from "components/stories/StoryQuestionTitle";
import FeedItemTitle from "components/feed/feed-item/preview/FeedItemTitle";

import Multiple from "./story-created/Multiple";
import QuickLinks from "./story-created/QuickLinks";
import Singular from "./story-created/Singular";

const MAX_NUMBER_TO_SHOW = 5;

const Story = ({
  userActivity,
  onStoryCheerToggle,
  onUserActivityUpdate,
  onUserActivityRemove
}) => {
  const [expanded, onExpand] = useExpand();
  const { profile } = useProfile();
  const { onToggleQueryStringValue } = useQueryString();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [activeStory, setActiveStory] = useState(null);

  // Primary User
  const user = userActivity.users[0];
  const { question } = userActivity.entity;

  // How many stories are in this feed item?
  const count = 1 + userActivity.childUserActivities.length;
  const isMultiple = count >= 2;

  const usersIds = getUniqueUsers(userActivity);
  const usersCount = usersIds.length;
  const currentUserAnswered = !!usersIds.find(
    userId => userId === profile.id
  );

  const handleThumbnailClick = _ => {
    setActiveStory(userActivity.entity);
    setModalIsOpen(true);
  };

  const handleClose = () => {
    setModalIsOpen(false);
  };

  const handleStoryUpdate = (updated, updatedUserActivity) => {
    setActiveStory(updated);
    onUserActivityUpdate({
      userActivityId: userActivity.id,
      updates: { entity: updated },
      userActivity: updatedUserActivity
    });
  };

  const handleCommentsChange = (_, numberOfComments) => {
    setActiveStory({ ...activeStory, commentCount: numberOfComments });
  };

  const handleStoryDelete = storyId => {
    onUserActivityRemove(storyId);
    handleClose();
  };

  const handleStoryCheerToggle = (storyId, cheerForUser, cheersCount) => {
    onStoryCheerToggle(storyId, cheerForUser, cheersCount);
  };

  const handleTagClick = tag => {
    onToggleQueryStringValue(getQueryStringKey(tag.entityType), tag.entity.id);
  };

  return (
    <>
      {activeStory && (
        <LightboxStories
          modalIsOpen={modalIsOpen}
          activeStory={activeStory}
          currentUserAnswered={currentUserAnswered}
          stories={[userActivity.entity]}
          onClose={handleClose}
          onCommentsChange={handleCommentsChange}
          onDelete={handleStoryDelete}
          onStoryCheerToggle={handleStoryCheerToggle}
          onStoryUpdate={handleStoryUpdate}
          isTrendsetter={!userActivity.parentUserActivity}
        />
      )}
      <div className="FeedItem__preview__photo_created Story">
        <FeedItemTitle item={userActivity} date={userActivity.bumpedAt}>
          <QuickLinks
            story={userActivity.entity}
            isMultiple={isMultiple}
            userActivity={userActivity}
            onUserActivityUpdate={onUserActivityUpdate}
          />
          <ProfileLink user={user} />
          {usersCount > 1 && (
            <span>
              <Trans>
                {" "}
                and {usersCount - 1} other{usersCount > 2 ? "s" : ""}
              </Trans>
            </span>
          )}
          <Trans> added their story</Trans>
        </FeedItemTitle>
        <StoryQuestionTitle storyQuestion={question} showLink />

        {!isMultiple && (
          <Singular
            item={userActivity}
            user={user}
            question={question}
            onClick={handleThumbnailClick}
            isTrendsetter={!userActivity.parentUserActivity}
          />
        )}
        {isMultiple && (
          <Multiple
            currentUserAnswered={currentUserAnswered}
            item={userActivity}
            question={question}
            onStoryCheerToggle={onStoryCheerToggle}
            onUserActivityUpdate={onUserActivityUpdate}
            onDelete={onUserActivityRemove}
          />
        )}
        <div className="chq-ffd-tags chq-ffd-tags__share">
          <div className="chq-ffd-tags__child">
            {question?.storyQuestionTags?.length === 0 && (
              <Badge className="Everyone--Tag">
                <Icon icon="global" className="category-icon" />
                Everyone
              </Badge>
            )}
            {expanded ? (
              <>
                {question?.storyQuestionTags?.map(
                  tag => tag.entity && (
                    <Badge
                      key={`${tag.id}`}
                      onClick={() => handleTagClick(tag)}
                    >
                      <Icon
                        icon={getIcon(tag.entityType)}
                        className="category-icon"
                      />
                      {tag.entity.name}
                    </Badge>
                  )
                )}
                <div key="minus-option" className="ExpandOptions__minus-option">
                  <span onClick={() => onExpand(false)}>
                    <Icon icon="minus" /> show less
                  </span>
                </div>
              </>
            ) : (
              <>
                {question?.storyQuestionTags?.map(
                  (tag, index) => tag.entity
                    && index < MAX_NUMBER_TO_SHOW && (
                    <Badge
                      key={`${tag.id}`}
                      onClick={() => handleTagClick(tag)}
                    >
                      <Icon
                        icon={getIcon(tag.entityType)}
                        className="category-icon"
                      />
                      {tag.entity.name}
                    </Badge>
                  )
                )}
                {question?.storyQuestionTags?.length > MAX_NUMBER_TO_SHOW && (
                  <div key="plus-option" className="ExpandOptions__plus-option">
                    <span onClick={() => onExpand(true)}>
                      <Icon icon="plus" />{" "}
                      {question.storyQuestionTags.length - MAX_NUMBER_TO_SHOW}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="FeedItem--Share__Story">
            <PostStory preFilledQuestion={question}>
              {onTrigger => (
                <Button
                  className="btn clickable FeedItem__preview__title"
                  inverted
                  onClick={onTrigger}
                >
                  <span>
                    <Icon icon="plus" />
                    <Trans>Add your story</Trans>
                  </span>
                </Button>
              )}
            </PostStory>
          </div>
        </div>
      </div>
    </>
  );
};

export default Story;
